'use client';

import React, { useRef, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { SectionLabel } from './styleConstants';
import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { drawImageSafe } from 'utils/drawImageSafe';
import styles from './ChargerIntroSection.module.css';

const MOBILE_BREAKPOINT = 1024;
const CHARGER_MOBILE_IMAGES = [
  'web_v2/ring-pro/charger-mobile/charger-1.png',
  'web_v2/ring-pro/charger-mobile/charger-2.png',
];

const SEQUENCE_PATH = '/web_v2/ring-pro/landing-page/sequence/pro-charger-ring';
const TOTAL_FRAMES = 305; // frames 0001–0305
const CONCURRENCY = 8;

// 0 = all frames, 1 = skip 1 (every 2nd), 2 = skip 2 (every 3rd), etc.
const FRAME_STEP = 2;

const frameUrl = (index: number) =>
  getCompressedAssetUrl(
    `${SEQUENCE_PATH}/${(index + 1).toString().padStart(4, '0')}.png`,
  );

const buildFrameIndices = () => {
  const step = FRAME_STEP + 1;
  const indices: number[] = [];
  for (let i = 0; i < TOTAL_FRAMES; i += step) {
    indices.push(i);
  }
  return indices;
};

/* ── Component ─────────────────────────────────────────── */

const ChargerIntroSection = () => {
  const t = useTranslations('ring-pro');
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const renderState = useRef({ frame: 0 });
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastDrawnFrameRef = useRef<number>(-1);
  const canvasDimensionsRef = useRef({ width: 0, height: 0 });
  const drawParamsRef = useRef({
    drawX: 0,
    drawY: 0,
    drawWidth: 0,
    drawHeight: 0,
  });
  const [isReady, setIsReady] = useState(false);
  const [isFirstFrameReady, setIsFirstFrameReady] = useState(false);
  const gsapCtx = useGsapContext();
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth > MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Preload PNG frames (desktop only) ─────────────── */

  useEffect(() => {
    if (!isDesktop) {
      (window as any).__chargerIntroLoaded = true;
      window.dispatchEvent(new Event('charger-intro-loaded'));
      return;
    }
    let isCancelled = false;
    const frameIndices = buildFrameIndices();
    const loaded: HTMLImageElement[] = new Array(frameIndices.length);

    const loadOne = (arrayIdx: number) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.loading = 'eager';
        (img as any).fetchPriority = 'high';
        img.src = frameUrl(frameIndices[arrayIdx]);
        img.onload = img.onerror = () => {
          loaded[arrayIdx] = img;
          resolve();
        };
      });

    let nextIdx = 0;
    let inflight = 0;

    const pump = () => {
      if (isCancelled) return;
      if (nextIdx >= frameIndices.length && inflight === 0) {
        imagesRef.current = loaded;
        console.log(
          `%c ChargerIntro %c Loaded ${loaded.length} images (FRAME_STEP=${FRAME_STEP}) `,
          'background:#059669;color:#fff;border-radius:3px 0 0 3px;padding:2px 6px;font-weight:bold',
          'background:#1e1e2e;color:#a5f3fc;border-radius:0 3px 3px 0;padding:2px 6px',
        );
        (window as any).__chargerIntroLoaded = true;
        window.dispatchEvent(new Event('charger-intro-loaded'));
        setIsReady(true);
        return;
      }
      while (inflight < CONCURRENCY && nextIdx < frameIndices.length) {
        const idx = nextIdx++;
        inflight++;
        loadOne(idx).then(() => {
          if (idx === 0) {
            imagesRef.current = loaded;
            setIsFirstFrameReady(true);
          }
          inflight--;
          pump();
        });
      }
    };

    pump();

    return () => {
      isCancelled = true;
    };
  }, [isDesktop]);

  /* ── Canvas draw helpers ─────────────────────────────── */

  const calculateDrawParams = () => {
    const displayWidth = canvasDimensionsRef.current.width;
    const displayHeight = canvasDimensionsRef.current.height;

    if (imagesRef.current.length === 0) return;
    const img = imagesRef.current[0];
    if (!img || img.naturalWidth === 0) return;

    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = displayWidth / displayHeight;

    let drawWidth: number;
    let drawHeight: number;
    let drawX: number;
    let drawY: number;

    if (imgAspect > canvasAspect) {
      drawWidth = displayWidth;
      drawHeight = drawWidth / imgAspect;
      drawX = 0;
      drawY = (displayHeight - drawHeight) / 2;
    } else {
      drawHeight = displayHeight;
      drawWidth = drawHeight * imgAspect;
      drawX = (displayWidth - drawWidth) / 2;
      drawY = 0;
    }

    drawParamsRef.current = { drawX, drawY, drawWidth, drawHeight };
  };

  const drawFrame = (frameIdx: number) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    const roundedIdx = Math.round(frameIdx);
    if (lastDrawnFrameRef.current === roundedIdx) return;

    const img = imagesRef.current[roundedIdx];
    if (!img || !img.complete || img.naturalWidth === 0) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const { drawX, drawY, drawWidth, drawHeight } = drawParamsRef.current;
    if (!drawImageSafe(ctx, img, drawX, drawY, drawWidth, drawHeight)) return;
    lastDrawnFrameRef.current = roundedIdx;
  };

  /* ── Canvas sizing ───────────────────────────────────── */

  useIsomorphicLayoutEffect(() => {
    if (!isFirstFrameReady || !canvasRef.current) return;
    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d', { desynchronized: true });
    if (!ctx) return;
    ctxRef.current = ctx;

    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvasDimensionsRef.current = { width: rect.width, height: rect.height };
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      calculateDrawParams();
      drawFrame(renderState.current.frame);
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    drawFrame(0);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isFirstFrameReady]);

  /* ── GSAP ScrollTrigger ──────────────────────────────── */

  useIsomorphicLayoutEffect(() => {
    if (
      !isReady ||
      !sectionRef.current ||
      !canvasRef.current ||
      imagesRef.current.length === 0
    ) {
      return;
    }

    gsapCtx.add(() => {
      const triggerEl = sectionRef.current;
      if (!triggerEl) return;
      const totalFrames = imagesRef.current.length - 1;
      const scrubObj = renderState.current;

      const tl = gsap.timeline({
        scrollTrigger: {
          immediateRender: false,
          trigger: triggerEl,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      const render = () => {
        drawFrame(scrubObj.frame);
      };

      const animationPortion = 0.8;

      tl.fromTo(
        scrubObj,
        { frame: 0 },
        {
          frame: totalFrames,
          ease: 'none',
          onUpdate: render,
          duration: animationPortion,
        },
        0,
      );

      tl.to({}, { duration: 1 - animationPortion }, animationPortion);

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
  }, [isReady, gsapCtx]);

  return (
    <section className={styles.section}>
      <div className={styles.textContainer}>
        <SectionLabel>{t('ring-pro.chargerIntro.label')}</SectionLabel>
        <h2 className={styles.heading}>{t('ring-pro.chargerIntro.heading')}</h2>
        <p className={styles.description}>
          {t('ring-pro.chargerIntro.description')}
        </p>
      </div>

      {isDesktop ? (
        <div className={styles.sequenceWrapper} ref={sectionRef}>
          <div className={styles.stickyContainer}>
            <canvas
              className={styles.sequenceCanvas}
              ref={canvasRef}
              role="img"
              aria-label={t('ring-pro.chargerIntro.canvasAriaLabel')}
            />
          </div>
        </div>
      ) : (
        <div className={styles.mobileCardsWrapper}>
          {CHARGER_MOBILE_IMAGES.map((src, i) => (
            <div className={styles.mobileCard} key={i}>
              <CustomImage
                className={styles.chargerImage}
                src={src}
                alt={`Ring PRO charging case ${i + 1}`}
                width={800}
                height={800}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ChargerIntroSection;
